import { __rest } from 'tslib';
import React, { useState, useRef, useEffect } from 'react';
import { styled, SvgIcon } from '@teko/ui';
import { formatCurrency } from './helpers.es.js';

const MAX_PRICE = 100000000000;
const StyledInput = styled.input(({ error, allowClear, prefixIcon, disabled, theme: { colors } }) => `
    //Remove input shadow on IOS Safari
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    box-sizing: border-box;
    margin: 0;
    font-variant: tabular-nums;
    list-style: none;
    font-feature-settings: 'tnum';
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 0;
    padding: 4px 10px;
    color: ${colors.text};
    font-size: 14px;
    line-height: 1.5715;
    background-image: none;
    border: 1px solid
      ${!!error ? colors.error : colors.border} !important;
    border-radius: 4px;
    transition: all 0.3s;
    ${allowClear ? 'padding-right: 32px' : ''};
    ${prefixIcon ? 'padding-left: 36px' : ''};
    ${!disabled ? 'background-color: #fff' : ''};

    &:hover {
      border-color: ${!!error ? colors.error : '#40a9ff'};
    }

    &:focus {
      border-color: ${!!error ? colors.error : '#40a9ff'};
      border-right-width: 1px !important;
      outline: 0;
      box-shadow: 0 0 0 2px
        ${!!error ? 'rgba(255,77,79,.2)' : 'rgba(24, 144, 255, 0.2)'};
    }
  `);
const InputContainer = styled.div `
  position: relative;
`;
const ClearIcon = styled(SvgIcon) `
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0.75rem;
  width: 1.25rem;
  height: 1.25rem;
  background-color: #bbb;
`;
const Label = styled.label(({ required, theme: { colors } }) => `
    margin-bottom: 0.4rem;
    font-weight: 500;
    &::before {
      content: '*';
      display: ${required ? 'inline-block' : 'none'};
      margin-right: 4px;
      color: ${colors.error};
      font-size: 14px;
      width: max-content;
    }
  `);
const Error = styled.div(({ theme: { colors } }) => `
    padding-top: 0;
    color: ${colors.error500};
    font-size: 0.75rem;
    line-height: 1rem;
    transition: color 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
    margin-top: 0.5rem;
  `);
const PrefixIcon = styled(SvgIcon) `
  position: absolute;
  top: 5px;
  left: 10px;
  cursor: pointer;
  z-index: 1;
`;
const Currency = styled.div `
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0.75rem;
`;
const Wrapper = styled.div `
  margin-bottom: 1rem;
`;
function resolveOnChange(target, e, onChange) {
    if (onChange) {
        let event = e;
        if (e.type === 'click') {
            event = Object.create(e);
            event.target = target;
            event.currentTarget = target;
            const originalInputValue = target.value;
            target.value = '';
            onChange(event);
            target.value = originalInputValue;
            return;
        }
        onChange(event);
    }
}
const Input = (_a) => {
    var { value, allowClear = true, label, required, error, name, prefixIcon, isFocused, allowFocus, inputType, currency, className, onChange } = _a, restProps = __rest(_a, ["value", "allowClear", "label", "required", "error", "name", "prefixIcon", "isFocused", "allowFocus", "inputType", "currency", "className", "onChange"]);
    const [internalValue, setInternalValue] = useState('');
    const textInputRef = useRef(null);
    useEffect(() => {
        if (inputType === 'price') {
            setInternalValue(formatCurrency(String(value)));
        }
        else {
            setInternalValue(value);
        }
    }, [value]);
    const handlePriceInput = (value) => {
        value = value.replace(/\./g, '');
        if (/^\d*$/.test(value))
            return value;
        return null;
    };
    const handleChange = (e) => {
        let value = e.target.value;
        if (inputType === 'price') {
            value = handlePriceInput(value);
            if (value !== null) {
                if (Number(value) > MAX_PRICE)
                    value = String(MAX_PRICE);
                e.target.value = value;
                resolveOnChange(textInputRef.current, e, onChange);
            }
        }
        else {
            setInternalValue(value);
            resolveOnChange(textInputRef.current, e, onChange);
        }
    };
    const handleClear = (e) => {
        setInternalValue('');
        textInputRef.current.focus();
        resolveOnChange(textInputRef.current, e, onChange);
    };
    useEffect(() => {
        isFocused && textInputRef.current.focus();
    }, [isFocused]);
    return (React.createElement(Wrapper, { className: className },
        label && (React.createElement(Label, { className: "input__label", required: required }, label)),
        React.createElement(InputContainer, null,
            prefixIcon && React.createElement(PrefixIcon, { name: prefixIcon, size: 22 }),
            React.createElement(StyledInput, Object.assign({ name: name, label: label, value: internalValue, allowClear: allowClear, onChange: handleChange, ref: textInputRef, error: error, prefixIcon: prefixIcon }, restProps, { className: "input__input" })),
            allowClear && !!internalValue && (React.createElement(ClearIcon, { name: "circle-close", onClick: handleClear })),
            !!currency && React.createElement(Currency, null, currency)),
        !!error && React.createElement(Error, { className: "input__error" }, error)));
};

export { Input as I };
